import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { serviceCategories } from '../pages/services/serviceCategories';
import logo from '../assets/logo.png';

function RenderIcon({ icon, size = 16, strokeWidth = 1.5 }) {
  if (!icon || typeof icon !== 'function') return null;
  const Icon = icon;
  return <Icon size={size} strokeWidth={strokeWidth} />;
}

const links = [
  { label: 'Process',    to: '/process' },
  { label: 'About Us',   to: '/about' },
  { label: 'Career',     to: '/careers' },
];

const industriesDropdown = [
  { label: 'Healthcare & Life Sciences', to: '/industries/healthcare' },
  { label: 'BFSI', to: '/industries/bfsi' },
  { label: 'Logistics & Transportation', to: '/industries/logistics' },
  { label: 'Retail & eCommerce', to: '/industries/retail' },
  { label: 'Manufacturing', to: '/industries/manufacturing' },
  { label: 'Real Estate & Hospitality', to: '/industries/real-estate-hospitality' },
];

const insightsDropdown = [
  { label: 'Case Studies', to: '/insights/case-studies' },
  { label: 'Use Cases', to: '/insights/use-cases' },
  { label: 'Blog', to: '/insights/blog' },
  { label: 'White Paper', to: '/insights/white-paper' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [hoveredService, setHoveredService] = useState(null);
  const location = useLocation();
  const servicesTimerRef = useRef(null);
  const industriesTimerRef = useRef(null);
  const insightsTimerRef = useRef(null);
  const submenuTimerRef = useRef(null);

  const servicesItems = useMemo(
    () => serviceCategories.map((c) => ({
      slug: c.slug,
      title: c.title,
      subServices: c.subServices || []
    })),
    []
  );

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setIndustriesOpen(false);
    setInsightsOpen(false);
    setHoveredService(null);
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (servicesTimerRef.current) window.clearTimeout(servicesTimerRef.current);
      if (industriesTimerRef.current) window.clearTimeout(industriesTimerRef.current);
      if (insightsTimerRef.current) window.clearTimeout(insightsTimerRef.current);
      if (submenuTimerRef.current) window.clearTimeout(submenuTimerRef.current);
    };
  }, []);

  const handleServicesMouseEnter = () => {
    if (servicesTimerRef.current) window.clearTimeout(servicesTimerRef.current);
    setServicesOpen(true);
  };

  const handleServicesMouseLeave = () => {
    if (servicesTimerRef.current) window.clearTimeout(servicesTimerRef.current);
    servicesTimerRef.current = window.setTimeout(() => {
      setServicesOpen(false);
      setHoveredService(null);
    }, 150);
  };

  const handleServiceHover = (slug) => {
    if (submenuTimerRef.current) window.clearTimeout(submenuTimerRef.current);
    setHoveredService(slug);
  };

  const handleServiceLeave = () => {
    if (submenuTimerRef.current) window.clearTimeout(submenuTimerRef.current);
    submenuTimerRef.current = window.setTimeout(() => setHoveredService(null), 150);
  };

  const handleIndustriesMouseEnter = () => {
    if (industriesTimerRef.current) window.clearTimeout(industriesTimerRef.current);
    setIndustriesOpen(true);
  };

  const handleIndustriesMouseLeave = () => {
    if (industriesTimerRef.current) window.clearTimeout(industriesTimerRef.current);
    industriesTimerRef.current = window.setTimeout(() => setIndustriesOpen(false), 150);
  };

  const handleInsightsMouseEnter = () => {
    if (insightsTimerRef.current) window.clearTimeout(insightsTimerRef.current);
    setInsightsOpen(true);
  };

  const handleInsightsMouseLeave = () => {
    if (insightsTimerRef.current) window.clearTimeout(insightsTimerRef.current);
    insightsTimerRef.current = window.setTimeout(() => setInsightsOpen(false), 150);
  };

  const handleMobileNavClick = () => {
    try {
      setOpen(false);
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  return (
    <motion.nav
      className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="navbar__inner">
        <Link to="/" className="navbar__logo">
          <img src={logo} alt="Kodefactor" className="navbar__logo-img" />
          <span className="brandmark">
            <span className="brandmark__name">Kodefactor</span>
            <span className="brandmark__sub">Consulting Pvt. Ltd.</span>
          </span>
        </Link>

        <ul className="navbar__links">
          {/* SERVICES DROPDOWN */}
          <li
            className="navbar__dropdown"
            onMouseEnter={handleServicesMouseEnter}
            onMouseLeave={handleServicesMouseLeave}
          >
            <NavLink to="/services" className="navbar__link navbar__dropdown-trigger">
              Services <ChevronDown size={16} className={`navbar__caret${servicesOpen ? ' navbar__caret--open' : ''}`} style={{ display: 'inline', marginLeft: '4px' }} />
            </NavLink>

            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  className="navbar__dropdown-menu"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  onMouseEnter={handleServicesMouseEnter}
                  onMouseLeave={handleServicesMouseLeave}
                >
                  {servicesItems.map((service) => (
                    <div
                      key={service.slug}
                      className="navbar__service-item"
                      onMouseEnter={() => handleServiceHover(service.slug)}
                      onMouseLeave={handleServiceLeave}
                    >
                      <NavLink
                        to={`/services/${service.slug}`}
                        className="navbar__dropdown-item"
                        onClick={() => {
                          setServicesOpen(false);
                          setHoveredService(null);
                        }}
                      >
                        {service.title}
                        {service.subServices.length > 0 && (
                          <ArrowRight size={16} className="navbar__submenu-arrow" />
                        )}
                      </NavLink>

                      <AnimatePresence>
                        {hoveredService === service.slug && service.subServices.length > 0 && (
                          <motion.div
                            className="navbar__submenu"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.15 }}
                            onMouseEnter={() => handleServiceHover(service.slug)}
                            onMouseLeave={handleServiceLeave}
                          >
                            {service.subServices.map((sub, idx) => {
                              const sectionId = sub.title
                                .toLowerCase()
                                .replace(/\s+/g, '-')
                                .replace(/[&]/g, 'and');
                              const SubIcon = sub.icon;
                              return (
                                <NavLink
                                  key={idx}
                                  to={`/services/${service.slug}#${sectionId}`}
                                  className="navbar__subitem"
                                  onClick={() => {
                                    setServicesOpen(false);
                                    setHoveredService(null);
                                  }}
                                >
                                  <span className="navbar__subitem-icon"><RenderIcon icon={SubIcon} size={16} strokeWidth={1.5} /></span>
                                  <span className="navbar__subitem-title">{sub.title}</span>
                                </NavLink>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                  <div className="navbar__dropdown-sep" />
                  <NavLink to="/services" className="navbar__dropdown-item navbar__dropdown-item--all">
                    View all services <ArrowRight size={14} style={{ display: 'inline', marginLeft: '4px' }} />
                  </NavLink>
                </motion.div>
              )}
            </AnimatePresence>
          </li>

          {/* INDUSTRIES DROPDOWN */}
          <li
            className="navbar__dropdown"
            onMouseEnter={handleIndustriesMouseEnter}
            onMouseLeave={handleIndustriesMouseLeave}
          >
            <NavLink to="/industries" className="navbar__link navbar__dropdown-trigger">
              Industries <ChevronDown size={16} className={`navbar__caret${industriesOpen ? ' navbar__caret--open' : ''}`} style={{ display: 'inline', marginLeft: '4px' }} />
            </NavLink>

            <AnimatePresence>
              {industriesOpen && (
                <motion.div
                  className="navbar__dropdown-menu"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  onMouseEnter={handleIndustriesMouseEnter}
                  onMouseLeave={handleIndustriesMouseLeave}
                >
                  {industriesDropdown.length > 0 ? (
                    industriesDropdown.map((industry, idx) => (
                      <NavLink
                        key={idx}
                        to={industry.to}
                        className="navbar__dropdown-item"
                        onClick={() => setIndustriesOpen(false)}
                      >
                        {industry.label}
                      </NavLink>
                    ))
                  ) : (
                    <div className="navbar__dropdown-item" style={{ color: '#999', cursor: 'default' }}>
                      Coming soon...
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </li>

          {/* INSIGHTS DROPDOWN */}
          <li
            className="navbar__dropdown"
            onMouseEnter={handleInsightsMouseEnter}
            onMouseLeave={handleInsightsMouseLeave}
          >
            <NavLink to="/insights" className="navbar__link navbar__dropdown-trigger">
              Insights <ChevronDown size={16} className={`navbar__caret${insightsOpen ? ' navbar__caret--open' : ''}`} style={{ display: 'inline', marginLeft: '4px' }} />
            </NavLink>

            <AnimatePresence>
              {insightsOpen && (
                <motion.div
                  className="navbar__dropdown-menu"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  onMouseEnter={handleInsightsMouseEnter}
                  onMouseLeave={handleInsightsMouseLeave}
                >
                  {insightsDropdown.map((item, idx) => (
                    <NavLink
                      key={idx}
                      to={item.to}
                      className="navbar__dropdown-item"
                      onClick={() => setInsightsOpen(false)}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </li>

          {/* OTHER LINKS */}
          {links.map((l) => (
            <li key={l.to}>
              <NavLink to={l.to} className="navbar__link">
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <Link
          to="/contact"
          className="btn btn--primary navbar__cta"
          onClick={() => {
            setOpen(false);
            setServicesOpen(false);
            setIndustriesOpen(false);
            setInsightsOpen(false);
          }}
          style={{
            display: 'inline-flex',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 0 40px rgba(37,99,235,0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '';
          }}
        >
          Contact
        </Link>

        <button
          className="navbar__hamburger"
          onClick={(e) => {
            e.preventDefault();
            setOpen(!open);
          }}
          aria-label="menu"
          type="button"
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="navbar__mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <NavLink to="/services" className="navbar__mobile-link" onClick={handleMobileNavClick}>
              Services
            </NavLink>
            <div className="navbar__mobile-sub">
              {servicesItems.map((c) => (
                <NavLink key={c.slug} to={`/services/${c.slug}`} className="navbar__mobile-sublink" onClick={handleMobileNavClick}>
                  {c.title}
                </NavLink>
              ))}
            </div>

            <NavLink to="/industries" className="navbar__mobile-link" onClick={handleMobileNavClick}>
              Industries
            </NavLink>
            {industriesDropdown.length > 0 && (
              <div className="navbar__mobile-sub">
                {industriesDropdown.map((industry, idx) => (
                  <NavLink key={idx} to={industry.to} className="navbar__mobile-sublink" onClick={handleMobileNavClick}>
                    {industry.label}
                  </NavLink>
                ))}
              </div>
            )}

            <NavLink to="/process" className="navbar__mobile-link" onClick={handleMobileNavClick}>
              Process
            </NavLink>

            <NavLink to="/insights" className="navbar__mobile-link" onClick={handleMobileNavClick}>
              Insights
            </NavLink>
            <div className="navbar__mobile-sub">
              {insightsDropdown.map((item, idx) => (
                <NavLink key={idx} to={item.to} className="navbar__mobile-sublink" onClick={handleMobileNavClick}>
                  {item.label}
                </NavLink>
              ))}
            </div>

            {links.map((l) => (
              <NavLink key={l.to} to={l.to} className="navbar__mobile-link" onClick={handleMobileNavClick}>
                {l.label}
              </NavLink>
            ))}

            <Link to="/contact" className="btn btn--primary" style={{ marginTop: 12 }} onClick={handleMobileNavClick}>
              Contact
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
